import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { LucideAngularModule, Delete } from 'lucide-angular';

/**
 * sr-pin-keypad · Teclado numérico de teclas grandes para la tablet de taller (se usa con guantes).
 *
 * No guarda nada: avisa cada número y el borrado, y quien lo usa decide. El teclado físico (el computador) lo atiende quien lo
 * usa, que es quien sabe si la pantalla está abierta; por eso acá existe `activa`, para que la tecla del teclado físico se vea
 * apretada igual que la del dedo.
 *
 * Diseño: docs/servicio-tecnico/diseno/SoftRent Modo Tecnico PIN.html (`.pn-kp`).
 *
 *   <sr-pin-keypad [deshabilitado]="validando()" [activa]="tecla()" (digito)="poner($event)" (borrar)="quitar()" />
 */
@Component({
  selector: 'sr-pin-keypad',
  standalone: true,
  imports: [LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="kp" [class.kp--off]="deshabilitado()" role="group" aria-label="Teclado numérico">
      @for (k of numeros; track k) {
        @if (k === '') {
          <span></span>
        } @else {
          <button type="button" class="kp__key" [class.kp__key--on]="activa() === k" [disabled]="deshabilitado()"
            [attr.aria-label]="'Número ' + k" (click)="digito.emit(k)">{{ k }}</button>
        }
      }
      <button type="button" class="kp__key kp__key--fn" [class.kp__key--on]="activa() === 'Backspace'" [disabled]="deshabilitado()"
        aria-label="Borrar el último número" (click)="borrar.emit()">
        <lucide-angular [img]="icons.Delete" [size]="28" /> Borrar
      </button>
    </div>
  `,
  styles: `
    :host { display: block; width: 100%; max-width: 400px; }
    .kp { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; width: 100%; --kh: 80px; }
    .kp--off { opacity: .35; }
    .kp__key {
      height: var(--kh); min-height: 56px; border-radius: var(--radius-xl); border: 2px solid var(--hairline-strong);
      background: var(--card); color: var(--ink); box-shadow: 0 2px 0 var(--hairline-strong); cursor: pointer;
      display: flex; align-items: center; justify-content: center; text-align: center;
      font-family: var(--font-mono); font-size: var(--fs-kiosk-key); font-weight: var(--fw-semibold);
      touch-action: manipulation; -webkit-tap-highlight-color: transparent; user-select: none;
    }
    .kp__key:focus-visible { outline: 3px solid var(--accent); outline-offset: 2px; }
    .kp__key:disabled { cursor: default; }
    .kp__key--fn {
      flex-direction: column; gap: 2px; background: var(--paper-tint); color: var(--ink-2);
      font-family: var(--font-body); font-size: var(--fs-base); font-weight: var(--fw-semibold);
    }
    .kp__key--on, .kp__key:active:not(:disabled) {
      background: var(--ink); color: var(--paper); border-color: var(--ink); box-shadow: none; transform: translateY(2px);
    }
    @media (min-width: 700px) and (orientation: portrait) { .kp { --kh: 92px; } }
    @media (max-width: 699px) { .kp { --kh: 74px; } }
    /* Teléfono o tablet en horizontal con poco alto: las 4 filas tienen que caber sin scroll. */
    @media (max-height: 560px) { .kp { --kh: 68px; gap: 8px; } }
    @media (max-height: 440px) { .kp { --kh: 56px; gap: 6px; } .kp__key { font-size: var(--fs-xl); } }
    @media (prefers-reduced-motion: reduce) { .kp__key--on, .kp__key:active:not(:disabled) { transform: none; } }
  `,
})
export class SrPinKeypadComponent {
  readonly deshabilitado = input(false);
  /** La tecla que se está apretando desde el teclado físico ('0'-'9' o 'Backspace'). */
  readonly activa = input<string | null>(null);

  readonly digito = output<string>();
  readonly borrar = output<void>();

  protected readonly icons = { Delete };
  protected readonly numeros = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0'];
}
